import { getProperty } from "@/lib/helpers.js";

import CONSTANTS from "@/Constants";
import { CustomizationRecord } from "@/Customizations/v2/customizations";
import { AccountingClass } from "@/typings/accounting";
import { Option } from "@/typings/globals";
const { APPOINTMENT_STATUS_LEGEND_ORDER } = CONSTANTS;

function countAccountingClasses(
  accountingClasses: Array<AccountingClass>,
  status: string
) {
  return accountingClasses.reduce((data: number, accountingClass) => {
    if (accountingClass.qbo_status === status) {
      data += 1;
    }

    return data;
  }, 0);
}

export default {
  internalUser({ currentAdmin }) {
    return currentAdmin?.internal || false;
  },
  financingEnabled({ tenant }) {
    return tenant?.feature_flags?.financing || false;
  },
  serviceFinanceEnabled({ tenant }) {
    return tenant?.service_finance_enabled || false;
  },
  paymentsAsLowAsEnabled({ tenant }) {
    return tenant?.feature_flags?.payments_as_low_as || false;
  },
  parentSubEnabled({ tenant }) {
    return tenant?.feature_flags?.parent_sub_account_ff_enabled || false;
  },
  configSupportedForTenant({ tenant }) {
    return (featureFlag) => {
      return Boolean(getProperty(tenant, featureFlag));
    };
  },
  featureEnabledForTenant({ tenant }) {
    return (featureFlag) => {
      if (!getProperty(tenant, "feature_flags") || !featureFlag) {
        return false;
      }
      return tenant.feature_flags[featureFlag] || false;
    };
  },
  settingEnabledForTenant({ tenant }) {
    return (featureFlag) => {
      if (!getProperty(tenant, featureFlag) || !featureFlag) {
        return false;
      }
      return tenant[featureFlag] || false;
    };
  },
  accountingProcessor({ tenant }) {
    return tenant?.accounting_processor;
  },
  showAccountingCategories({ tenant }) {
    return tenant?.accounting_processor === "saasant";
  },
  showAccountingClasses({ tenant }) {
    return tenant?.track_classes_by.length > 0;
  },
  trackClassesByInvoice({ tenant }) {
    return tenant?.track_classes_by.includes("invoice");
  },
  trackClassesByLineItem({ tenant }) {
    return tenant?.track_classes_by.includes("line_item");
  },
  activeCalls: ({ tenantCalls, currentAdmin }) => {
    return tenantCalls
      .filter(
        (call) =>
          getProperty(call, "claimed_by.id") === currentAdmin.id &&
          !call.session_ended_at
      )
      .sort((a, b) => {
        return new Date(b.started_at) < new Date(a.started_at) ? 1 : -1;
      });
  },
  jobStatusEnabled({ tenant }) {
    return getProperty(tenant, "job_status_enabled");
  },
  sortedAppointmentStatusCustomizations: ({ tenantCustomizations }) => {
    if (!tenantCustomizations) {
      return {};
    }
    const statuses = tenantCustomizations["Schedule Colors"];
    return Object.fromEntries(
      Object.entries(statuses).sort((a, b) => {
        return (
          APPOINTMENT_STATUS_LEGEND_ORDER[a[0]] -
          APPOINTMENT_STATUS_LEGEND_ORDER[b[0]]
        );
      })
    ) as { [k: string]: CustomizationRecord };
  },
  accountingClassOptions: ({ accountingClasses }): Array<Option<number>> => {
    const accountingClassList: Array<AccountingClass> =
      Object.values(accountingClasses);

    return accountingClassList.map((ac) => ({
      value: ac.id,
      label: ac.name,
      archived: ac.archived
    }));
  },
  pendingAccountingClasses: ({ accountingClasses }): number => {
    return countAccountingClasses(Object.values(accountingClasses), "pending");
  },
  verifiedAccountingClasses: ({ accountingClasses }): number => {
    return countAccountingClasses(Object.values(accountingClasses), "verified");
  },
  inactiveAccountingClasses: ({ accountingClasses }): number => {
    return countAccountingClasses(Object.values(accountingClasses), "inactive");
  },
  permissionKeys: ({ accessRole }): Array<string> => {
    if (!accessRole) return [];
    return accessRole.permissions.map((permission) => permission.key);
  }
};
